import {graphForTextureSet} from "#material/constants/graph-for-texture-set"
import {deepTraverseMaterialNodeParameters} from "#material/helpers/deep-traverse-material-node-parameters"
import type {Evaluator} from "@cm/graph-v3"
import {registerMaterialGraphNode} from "#material/register"
import {BaseGraphNode, GraphNode, TraversalAction, traverseGraphParameters} from "@cm/graph-v3"
import type {MaterialGraphCyclesReturns, MaterialGraphParameters, MaterialGraphThreeReturns} from "#material/nodes/base/graph/definition"
import {materialGraphDefinition} from "#material/nodes/base/graph/definition"
import type {CyclesContext, MaterialContext, ThreeContext} from "#material/types/context"
import {OutputMaterial} from "#material/nodes/base/output/output-material"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {MaterialConnection} from "#material/nodes/base/connection/material-connection"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesNode} from "#material/cycles/types"
import type {MaterialNodeFlattenedReturns, MaterialNodeParameters} from "#material/sockets/parameters"
import type {DataResolver} from "#material/validation/data-resolver/index"

@registerMaterialGraphNode
export class MaterialGraph extends GraphNode<MaterialGraphThreeReturns | MaterialGraphCyclesReturns, MaterialContext, MaterialGraphParameters> {
    public override get definition() {
        return materialGraphDefinition
    }

    async compileToThree(evaluator: Evaluator<ThreeContext>): Promise<MaterialGraphThreeReturns> {
        const rootNode = this.rootNode
        if (rootNode instanceof OutputMaterial) {
            const threeResult = await rootNode.toThree(evaluator as ThreeEvaluator)
            return threeResult.surface
        }
        throw new Error(`MaterialGraph must have an OutputMaterial node as its root node`)
    }

    async compileToCycles(evaluator: Evaluator<CyclesContext>): Promise<MaterialGraphCyclesReturns> {
        const processedNodes = new Set<CyclesNode>()
        const compressCyclesNode = (node: CyclesNode) => {
            if (processedNodes.has(node)) return
            processedNodes.add(node)
            for (const key of Object.keys(node.inputs)) {
                const input = node.inputs[key]
                if (input === undefined) delete node.inputs[key]
                else {
                    compressCyclesNode(input[0])
                    if (node.parameters[key] !== undefined) delete node.parameters[key]
                }
            }
            for (const key of Object.keys(node.parameters)) {
                const parameter = node.parameters[key]
                if (parameter === undefined) delete node.parameters[key]
            }
        }

        const rootNode = this.rootNode
        if (rootNode instanceof OutputMaterial) {
            const cyclesResult = await rootNode.toCycles(evaluator as CyclesEvaluator)
            const name = this.parameters.name
            const rootNodeData = cyclesResult.surface[0]
            compressCyclesNode(rootNodeData)
            return {
                ...rootNodeData,
                parameters: {
                    ...rootNodeData.parameters,
                    name: rootNodeData.parameters.name ?? name,
                },
            }
        }
        throw new Error(`MaterialGraph must have an OutputMaterial node as its root node`)
    }

    get rootNode() {
        const rootNode = this.parameters.nodes[0]
        if (rootNode instanceof OutputMaterial) {
            return rootNode
        }
        return null
    }

    get nodes(): Readonly<MaterialNode[]> {
        return this.parameters.nodes as readonly MaterialNode[]
    }
    set nodes(nodes: MaterialNode[]) {
        this.updateParameters({nodes})
    }

    get connections(): Readonly<MaterialConnection<any>[]> {
        return this.parameters.connections as readonly MaterialConnection<any>[]
    }

    static forTextureSetRevision = (textureSetRevisionId?: string) => {
        return MaterialGraph.fromOutputMaterial(graphForTextureSet(textureSetRevisionId))
    }

    static empty = () => {
        const nodes = [new OutputMaterial({parameters: {inputs: {}, settings: {}}})]
        return new MaterialGraph({parameters: {nodes, connections: []}})
    }

    public static fromOutputMaterial(outputMaterial: OutputMaterial, name?: string): MaterialGraph {
        const nodes: MaterialNode[] = []
        const connections: MaterialConnection<any>[] = []
        deepTraverseMaterialNodeParameters(outputMaterial, (nodeOrConnection) => {
            if (nodeOrConnection instanceof MaterialNode) {
                nodes.push(nodeOrConnection)
            } else if (nodeOrConnection instanceof MaterialConnection) {
                connections.push(nodeOrConnection)
            }
        })
        return new MaterialGraph({parameters: {nodes, connections, name}})
    }

    public applyDefaults() {
        this.nodes.forEach((node) => node.applyDefaults())
    }

    public addNode(node: MaterialNode) {
        if (this.parameters.nodes.some((existingNode) => existingNode.id === node.id)) {
            return
        }
        this.updateParameters({nodes: [...this.parameters.nodes, node]})
    }

    public removeNode(node: MaterialNode) {
        // Remove all connections involving this node
        const connectionsToRemove = this.parameters.connections.filter((connection) => {
            const data = connection.data
            return data.source.nodeId === node.id || data.destination.nodeId === node.id
        })

        // Clean up connections and unset inputs on connected nodes
        connectionsToRemove.forEach((connection) => {
            // Remove the connection from the graph
            this.removeConnection(connection)
        })

        // Remove the node itself
        this.updateParameters({nodes: this.parameters.nodes.filter((materialNode) => materialNode.id !== node.id)})
    }

    public addConnection(connection: MaterialConnection<any>) {
        if (
            this.parameters.connections.some(
                (existingConnection) =>
                    existingConnection.id === connection.id ||
                    (existingConnection.source?.id === connection.source?.id &&
                        existingConnection.sourceParameter === connection.sourceParameter &&
                        existingConnection.destination?.id === connection.destination?.id &&
                        existingConnection.destinationParameter === connection.destinationParameter),
            )
        ) {
            return
        }
        this.updateParameters({connections: [...this.parameters.connections, connection]})
    }

    public removeConnection(connection: MaterialConnection<any>) {
        const data = connection.data

        const destinationNode = this.parameters.nodes.find((n) => n.id === data.destination.nodeId)
        if (destinationNode && data.destination.socketId) {
            destinationNode.unsetInput(data.destination.socketId)
        }

        this.updateParameters({connections: this.parameters.connections.filter((materialNode) => materialNode.id !== connection.id)})
    }

    override replaceParameters(newParameters: MaterialGraphParameters, disableValidation?: boolean): void {
        super.replaceParameters(newParameters, disableValidation as true)
        this.recomputeHash()
    }

    public isShadowCatcherMaterial(): boolean {
        return !!this.rootNode?.parameters.settings.shadowCatcher
    }

    private _uniqueId: string | undefined = undefined
    private _skipDirtyMark = false

    // this is used to determine if the graph has changed
    // we cache it for performance reasons - call `recomputeHash` if the graph changes
    get uniqueId() {
        if (this._uniqueId === undefined) {
            this._uniqueId = this.getHash()
        }
        return this._uniqueId
    }

    public markDirty() {
        if (this._skipDirtyMark) return
        this.updateNodesAndConnections()
    }

    override clone(options?: {
        cloneSubNode?: (parent: BaseGraphNode, subNode: BaseGraphNode, path: string) => boolean
        parameterOverrides?: Partial<MaterialGraphParameters> | ((node: BaseGraphNode) => MaterialGraphParameters | undefined)
        cloneInstanceIds?: boolean
        beforeReplaceClonedSubNodes?: (clonedNode: BaseGraphNode) => void
    }): this {
        const result = super.clone({
            ...options,
            beforeReplaceClonedSubNodes: (clonedNode) => {
                ;(clonedNode as MaterialGraph)._skipDirtyMark = true
                options?.beforeReplaceClonedSubNodes?.(clonedNode)
            },
        })
        result._skipDirtyMark = false
        return result
    }

    public onHashInvalidated: (() => void) | undefined

    public recomputeHash() {
        this._uniqueId = undefined
        if (!this._skipDirtyMark) {
            this.onHashInvalidated?.()
        }
    }

    public updateNodesAndConnections() {
        const nodes: MaterialNode[] = [...this.nodes]
        const existingNodes = new Set(nodes)
        const connections: MaterialConnection<any>[] = [...this.connections]
        const existingConnections = new Set(connections)
        deepTraverseMaterialNodeParameters([...this.nodes, ...this.connections], (node) => {
            if (node instanceof MaterialNode) {
                if (!existingNodes.has(node)) {
                    nodes.push(node)
                }
            } else if (node instanceof MaterialConnection) {
                if (!existingConnections.has(node)) {
                    connections.push(node)
                }
            }
        })
        // Directly set without triggering updateParameters to avoid infinite update cycle
        const parameters = this.parameters as MaterialGraphParameters
        parameters.nodes = nodes
        parameters.connections = connections
        this.updateChildren()
        this.recomputeHash()
    }

    public async flatten(resolve: DataResolver): Promise<MaterialGraph> {
        if (!this.parameters.nodes.some((n) => n.hierarchical)) return this

        const rootNode = this.rootNode
        if (!rootNode) return MaterialGraph.empty()

        const nodes = new Set<MaterialNode>()
        deepTraverseMaterialNodeParameters(rootNode, (nodeOrConnection) => {
            if (nodeOrConnection instanceof MaterialNode) {
                nodes.add(nodeOrConnection)
            }
        })

        const flattenedNodes = new Map<MaterialNode, MaterialNode | MaterialNodeFlattenedReturns<any>>(
            await Promise.all(
                [...nodes].map(async (node) => {
                    if (node.hierarchical) {
                        const flattenedNode = await node.flatten(resolve)
                        return [node, flattenedNode] as const
                    } else return [node, node.clone({cloneSubNode: () => false})] as const
                }),
            ),
        )

        const remapNode = (node: MaterialNode) => {
            const updatedParameters = traverseGraphParameters(
                node.parameters,
                (value) => value instanceof BaseGraphNode,
                (connection) => {
                    if (connection instanceof MaterialConnection) {
                        const mappedNode = flattenedNodes.get(connection.parameters.value)
                        if (mappedNode) {
                            if (mappedNode instanceof MaterialNode)
                                return new MaterialConnection({
                                    parameters: {
                                        key: connection.parameters.key,
                                        value: mappedNode,
                                    },
                                })
                            else return mappedNode[connection.parameters.key].clone({cloneSubNode: () => false})
                        } else return
                    } else {
                        throw new Error("Expected MaterialConnection")
                    }
                },
            ) as MaterialNodeParameters<any>

            node.replaceParameters(updatedParameters)
        }

        const nodesToRemap = new Set<MaterialNode>()
        for (const mappedNode of flattenedNodes.values()) {
            if (mappedNode instanceof MaterialNode) nodesToRemap.add(mappedNode)
            else {
                for (const paramNode of Object.values(mappedNode)) {
                    paramNode.depthFirstTraversalPreorder((node) => {
                        if (node instanceof MaterialNode) nodesToRemap.add(node)
                        return TraversalAction.Continue
                    }, nodes)
                }
            }
        }

        for (const mappedNode of nodesToRemap) {
            remapNode(mappedNode)
        }

        const mappedRootNode = flattenedNodes.get(rootNode)
        if (!(mappedRootNode instanceof OutputMaterial)) {
            throw new Error("Expected flattened root node to be an OutputMaterial")
        }

        const result = MaterialGraph.fromOutputMaterial(mappedRootNode)

        const {nodes: _, connections: __, ...rest} = this.parameters
        result.updateParameters(rest)

        if (result.parameters.nodes.some((n) => n.hierarchical)) return result.flatten(resolve)
        else return result
    }
}
